/* eslint @typescript-eslint/no-unused-vars: 'off' -- Destructure to simplify selector return values */
import {
    LocationsInspectionSummary,
    LocationsOccupantDetail,
    OccupantDetail,
    LocationsProperty,
    LocationsOccupant,
    LocationsSpaceOccupant,
    LocationsSpace,
} from '@centecomm/portal-service';
import { createSelector } from 'reselect';
import { LoadStatus, State, StateRecord, StateSelector } from '../../Types';
import { Inspection } from '../Inspections/Types/Inspection';
import { InspectionCategories } from '../Inspections/Types/InspectionCategories';
import { InspectionItem } from '../Inspections/Types/InspectionItem';
import { InteractionItem } from '../Inspections/Types/InteractionItem';
import { Note } from '../Inspections/Types/Note';
import { Photo } from '../Inspections/Types/Photo';
import isInspectionItem from '../Inspections/Types/TypeGuards/isInspectionItem';
import isInteraction from '../Inspections/Types/TypeGuards/isInteraction';
import isNote from '../Inspections/Types/TypeGuards/isNote';
import isPhoto from '../Inspections/Types/TypeGuards/isPhoto';
import { LocationInspection } from './Types/LocationInspection';

const locationsState = ({ locations }: State) => locations;

type InspectionId = number;
type AggregateItem = InspectionItem | InteractionItem;

const dateTime = (date: string | undefined): number => new Date(date || Date.now()).getTime();

// Properties
const propertiesSlice = createSelector(locationsState, ({ properties }): StateRecord<LocationsProperty> => properties);

export const propertiesLoadStatus = createSelector(propertiesSlice, ({ loadStatus }): LoadStatus => loadStatus);

export const propertiesAreLoaded = createSelector(
    propertiesLoadStatus,
    (loadStatus): boolean => loadStatus === LoadStatus.LOADED,
);

export const properties = createSelector(
    propertiesSlice,
    ({
        loadStatus,
        sortOrder,
        errorState = {},
        ...rest
    }): Omit<StateRecord<LocationsProperty>, 'loadStatus' | 'sortOrder' | 'errorState'> => rest,
);

export const propertiesList = createSelector(
    propertiesSlice,
    ({ loadStatus, sortOrder, errorState = {}, ...rest }): LocationsProperty[] => Object.values(rest),
);

export const sortedPropertiesList = createSelector(propertiesSlice, ({ sortOrder = [], ...rest }): LocationsProperty[] =>
    sortOrder.map((i) => rest[i]),
);

export const property = (propertyId: number | string): StateSelector<LocationsProperty | null> =>
    createSelector(
        propertiesSlice,
        (propertyProperties): LocationsProperty | null => propertyProperties[propertyId] || null,
    );

export const propertyOccupants = (propertyId: number | string): StateSelector<LocationsOccupant[]> =>
    createSelector(
        property(propertyId),
        (propertyOccupantsProperty): LocationsOccupant[] =>
            (propertyOccupantsProperty && propertyOccupantsProperty.occupants) || [],
    );

// Occupants
const occupantsSlice = createSelector(locationsState, ({ occupants }): Record<number, LocationsOccupantDetail> => occupants);

export const occupantDetails = (occupantId: number | string): StateSelector<LocationsOccupantDetail | null> =>
    createSelector(occupantsSlice, (occupants): LocationsOccupantDetail | null => occupants[occupantId] || null);

export const occupantDetailsLoaded = (occupantId: number | string): StateSelector<boolean> =>
    createSelector(occupantsSlice, (occupants): boolean => Object.hasOwnProperty.call(occupants, occupantId));

export const certificateOfInsuranceNotRequired = (occupantId: number | string): StateSelector<boolean> =>
    createSelector(occupantDetails(occupantId), (occ) => !!occ && occ.certificateOfInsuranceManualCompliance);

// Spaces
const spacesSlice = createSelector(locationsState, ({ spaces }): Record<number, StateRecord<LocationsSpace>> => spaces);

export const spacesForProperty = (propertyId: number | string): StateSelector<StateRecord<LocationsSpace> | null> =>
    createSelector(spacesSlice, (spaces): StateRecord<LocationsSpace> | null => spaces[propertyId] || null);

export const sortedSpacesForProperty = (propertyId: number | string): StateSelector<LocationsSpace[]> =>
    createSelector(spacesForProperty(propertyId), (spaces): LocationsSpace[] => {
        if (spaces) {
            const { sortOrder = [], loadStatus, errorState = {}, ...rest } = spaces;

            return sortOrder.map((id) => rest[id]);
        }

        return [];
    });

export const spacesLoadedForProperty = (propertyId: number | string): StateSelector<boolean> =>
    createSelector(spacesSlice, (spaces): boolean => Object.hasOwnProperty.call(spaces, propertyId));

export const space = (propertyId: number | string, spaceId: number | string): StateSelector<LocationsSpace | null> =>
    createSelector(spacesForProperty(propertyId), (spaces): LocationsSpace | null => (spaces && spaces[spaceId]) || null);

// Space Occupants
const spaceOccupantsSlice = createSelector(
    locationsState,
    ({ spaceOccupants }): Record<number, OccupantDetail[]> => spaceOccupants,
);

export const spaceOccupants = (spaceId: number | string): StateSelector<LocationsSpaceOccupant[]> =>
    createSelector(
        spaceOccupantsSlice,
        (spaceOccupantsSliceForOccupants): LocationsSpaceOccupant[] => spaceOccupantsSliceForOccupants[spaceId] || [],
    );

export const occupantsLoadedForSpace = (spaceId: number | string): StateSelector<boolean> =>
    createSelector(spaceOccupantsSlice, (occupantsLoadedForSpaceSlice): boolean =>
        Object.hasOwnProperty.call(occupantsLoadedForSpaceSlice, spaceId),
    );

// Inspections
const inspectionsSlice = createSelector(
    locationsState,
    ({ inspections }): Record<InspectionId, LocationInspection> => inspections,
);

export const inspection = (inspectionId: number | string): StateSelector<LocationInspection | null> =>
    createSelector(inspectionsSlice, (inspections: Record<InspectionId, LocationInspection>): LocationInspection | null => {
        if (Object.hasOwnProperty.call(inspections, inspectionId)) return inspections[inspectionId];

        return null;
    });

export const inspectionLoaded = (inspectionId: number | string): StateSelector<boolean> =>
    createSelector(inspectionsSlice, (inspections: Record<number, LocationInspection>): boolean =>
        Object.hasOwnProperty.call(inspections, inspectionId),
    );

export const inspectionItems = (inspectionId: number | string): StateSelector<Array<InspectionItem | InteractionItem>> =>
    createSelector(
        inspectionsSlice,
        (inspections: Record<InspectionId, Inspection>): Array<InspectionItem | InteractionItem> => {
            if (Object.prototype.hasOwnProperty.call(inspections, inspectionId)) {
                return [...inspections[inspectionId].items, ...inspections[inspectionId].interactions].sort(
                    (a: AggregateItem, b: AggregateItem) => dateTime(a.createdDate) - dateTime(b.createdDate),
                );
            }

            return [];
        },
    );

export const notes = (inspectionId: number | string): StateSelector<Note[]> =>
    createSelector(
        inspectionItems(inspectionId),
        (notesInspectionItems: Array<InspectionItem | InteractionItem>): InspectionItem[] =>
            notesInspectionItems
                .filter((item) => isInspectionItem(item))
                .filter((item: InspectionItem) => isNote(item)) as Note[],
    );

export const photos = (inspectionId: number | string): StateSelector<Photo[]> =>
    createSelector(
        inspectionItems(inspectionId),
        (photosInspectionItems: Array<InspectionItem | InteractionItem>): InspectionItem[] =>
            photosInspectionItems
                .filter((item) => isInspectionItem(item))
                .filter((item: InspectionItem) => isPhoto(item)) as Photo[],
    );

export const interactions = (inspectionId: number | string): StateSelector<InteractionItem[]> =>
    createSelector(
        inspectionItems(inspectionId),
        (interactionsInspectionItems: Array<InspectionItem | InteractionItem>): InteractionItem[] =>
            interactionsInspectionItems.filter((item) => isInteraction(item)) as InteractionItem[],
    );

export const itemsByCategory = (
    inspectionId: number | string,
    categoryId: InspectionCategories,
): StateSelector<Array<InspectionItem | InteractionItem>> =>
    createSelector(
        inspectionItems(inspectionId),
        (categoryInspectionItems: Array<InspectionItem | InteractionItem>): Array<InspectionItem | InteractionItem> =>
            categoryInspectionItems.filter((item: InspectionItem | InteractionItem) => {
                if (isInspectionItem(item)) {
                    return !!item.categoryId && `${item.categoryId}` === `${categoryId}`;
                }

                return item.items.some(
                    (interactionItem: InspectionItem) =>
                        interactionItem.categoryId && `${interactionItem.categoryId}` === `${categoryId}`,
                );
            }),
    );

// Inspection Summaries
const inspectionSummariesSlice = createSelector(
    locationsState,
    ({ inspectionSummaries }): Record<number, LocationsInspectionSummary[]> => inspectionSummaries,
);

export const propertyInspectionSummaries = (propertyId: number | string): StateSelector<LocationsInspectionSummary[]> =>
    createSelector(
        inspectionSummariesSlice,
        (inspectionSummaries: Record<number, LocationsInspectionSummary[]>): LocationsInspectionSummary[] => {
            if (Object.hasOwnProperty.call(inspectionSummaries, propertyId)) return inspectionSummaries[propertyId];

            return [];
        },
    );

export const inspectionSummariesLoadedForProperty = (propertyId: number | string): StateSelector<boolean> =>
    createSelector(inspectionSummariesSlice, (inspectionSummaries: Record<number, LocationsInspectionSummary[]>): boolean =>
        Object.hasOwnProperty.call(inspectionSummaries, propertyId),
    );
